import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchAPI } from '../utils/api';
import Navbar from '../components/home/Navbar';
import FloatingAction from '../components/home/FloatingAction';
import { FaGraduationCap, FaStar, FaQuoteLeft, FaUser, FaPen } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

export default function StudentFeedback() {
    const { currentUser } = useAuth();
    const [feedbackData, setFeedbackData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadFeedback = async () => {
            try {
                const data = await fetchAPI('/public/get_feedback.php');
                setFeedbackData(data);
            } catch (err) {
                console.error("Failed to load feedback", err);
            } finally {
                setLoading(false);
            }
        };
        loadFeedback();
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 100 }
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans text-gray-900">
            <Navbar />

            {/* Header Section */}
            {/* <div className="relative bg-blue-600 text-white py-20 overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
                            Student <span className="text-yellow-400">Success Stories</span>
                        </h1>
                        <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto mb-8 font-medium">
                            Discover how SudeshMaths has transformed the learning journey of students across the island.
                        </p>
                    </motion.div>
                </div>
            </div> */}

            {/* Testimonials Grid */}
            <div className="container mx-auto px-6 py-16">
                {loading ? (
                    <div className="text-center py-20 text-gray-500 italic">Loading inspiring stories...</div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6"
                    >
                        {feedbackData.map((feedback) => (
                            <motion.div
                                key={feedback.id}
                                variants={itemVariants}
                                className="break-inside-avoid bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300 relative group"
                            >
                                {/* Quote Icon */}
                                <div className="absolute top-6 right-6 text-blue-50 opacity-50 group-hover:opacity-100 transition-opacity">
                                    <FaQuoteLeft className="text-4xl" />
                                </div>

                                {/* Header: User Info & Sentiment */}
                                <div className="flex items-center gap-4 mb-4 relative z-10">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md">
                                        {feedback.author.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 leading-tight">{feedback.author}</h3>
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${feedback.sentiment === 'satisfied' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            {feedback.sentiment === 'satisfied' ? 'Verified Student' : 'Student'}
                                        </span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="relative z-10">
                                    <h4 className="font-bold text-gray-800 text-lg mb-2 leading-snug">
                                        {feedback.headline}
                                    </h4>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                        {feedback.content}
                                    </p>
                                </div>

                                {/* Footer: Tags & Time */}
                                <div className="pt-4 border-t border-gray-50 flex flex-wrap items-center justify-between gap-2 relative z-10">
                                    <div className="flex gap-2">
                                        {feedback.tags && feedback.tags.slice(0, 2).map((tag, i) => (
                                            <span key={i} className="text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-1 rounded-md">
                                                #{tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-xs text-gray-400 font-medium">
                                        {feedback.time}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>

            <FloatingAction />
        </div>
    );
}
